// src/screens/deliveryPanel/notifications/DeliveryNotificationsList.jsx
import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import { MdNotifications, MdSearch, MdClose } from "react-icons/md";

// Reuse Components
import Pagination from "../../../components/general/pagination/Pagination";
import StatCard from "../../../components/admin/statcard/StatCard";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";

// CSS
import "../../../assets/css/delivery/deliveryNotifications/DeliveryNotifications.css";
import DeliveryPanelLayout from "../../../layouts/DeliveryPanelLayout";
import MyContext from "../../../context/MyContext";
import { NotificationsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import { getEcho } from "../../../components/general/echo/Echo";

function DeliveryNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Notifications");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { baseUrl, authToken, loggedInDeliveryMan } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [notifStats, setNotifStats] = useState({ total: 0, unread: 0, read: 0 });

  //----------API Calling — ab GET + Bearer token (Topbar jaisa), delivery
  // controller khud auth('Kmdeliveryman')->user() se apni notifications nikalta hai
  const viewAllNotifications = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/delivery/notifications/viewAllNotifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setNotifications(response.data.notifications || []);
          // ── NEW: stat cards ab seedha backend se aane wale counts
          // use karte hain, notifications array ko filter karke khud
          // calculate nahi karte ──
          setNotifStats(response.data.stats || { total: 0, unread: 0, read: 0 });
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications.",
        });
      });
  };

  const changeNotificationStatus = (notifications) => {
    const formData = new FormData();
    formData.append("id", notifications.id);

    axios
      .post(
        `${baseUrl}/delivery/notifications/changeNotificationStatus`,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification status successfully updated!",
          });
          viewAllNotifications();
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
        }
      });
  };

  const deleteNotification = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This notification will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", id);
        axios
          .post(
            `${baseUrl}/delivery/notifications/deleteNotification`,
            formData,
            { headers: { Authorization: `Bearer ${authToken}` } },
          )
          .then((response) => {
            if (response.data.success == true) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Notification deleted successfully!",
              });
              viewAllNotifications();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to delete notification.",
              });
            }
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              window.location.href = "/delivery/login";
              return;
            }
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete notifications.",
            });
          });
      }
    });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/delivery/login");
      return;
    }
    viewAllNotifications();
  }, [authToken]);

  // ── real-time — nayi notification aate hi list ke top pe daal do,
  // poori list dobara fetch karne ki zaroorat nahi ──
  useEffect(() => {
    if (!authToken || !loggedInDeliveryMan?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `deliveryman.${loggedInDeliveryMan.id}`;
    const channel = echo.private(channelName);

    channel.listen(".new-notification", (e) => {
      const incoming = e.notification;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInDeliveryMan?.id]);

  // Stats Data — ab seedha backend se aane wale counts use ho rahe hain
  // (notifStats), notifications array filter nahi hota
  const stats = [
    {
      label: "Total Notifications",
      value: notifStats.total.toString(),
      icon: MdNotifications,
      color: "purple",
    },
    {
      label: "Unread",
      value: notifStats.unread.toString(),
      icon: MdNotifications,
      color: "orange",
    },
    {
      label: "Read",
      value: notifStats.read.toString(),
      icon: MdNotifications,
      color: "green",
    },
  ];

  // Filters
  const filteredNotifications = (notifications || []).filter((notification) => {
    const matchesSearch =
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All Notifications" ||
      (statusFilter === "Unread"
        ? String(notification.status).toLowerCase() !== "read"
        : String(notification.status).toLowerCase() === "read");
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = filteredNotifications.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  if (loading) {
    return (
      <DeliveryPanelLayout>
        <NotificationsPageSkeleton />
      </DeliveryPanelLayout>
    );
  }

  return (
    <DeliveryPanelLayout>
      <div className="del-notifications-page">
        <Container fluid>
          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            {stats.map((stat, index) => (
              <Col key={index} xs={12} sm={6} lg={4}>
                <StatCard {...stat} />
              </Col>
            ))}
          </Row>

          {/* Filters */}
          <Row className="mb-3">
            <Col xs={12}>
              <div className="del-filters-wrapper">
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={8} lg={9}>
                    <div className="del-search-box">
                      <span className="del-search-icon">
                        <MdSearch />
                      </span>
                      <input
                        type="text"
                        className="del-form-control"
                        placeholder="Search notifications..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <select
                      className="del-form-control del-select"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option>All Notifications</option>
                      <option>Unread</option>
                      <option>Read</option>
                    </select>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Notifications Table */}
          <Row>
            <Col xs={12}>
              <div className="del-table-responsive">
                <table className="del-table">
                  <thead className="del-table-head">
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="del-table-body">
                    {currentNotifications.map((n) => (
                      <tr
                        key={n.id}
                        className={
                          String(n.status).toLowerCase() !== "read" ? "del-unread-row" : ""
                        }
                      >
                        <td>{n.id}</td>
                        <td>{n.title}</td>
                        <td className="del-message-preview">{n.message}</td>
                        <td>{(n.created_at || n.date)?.split("T")[0]}</td>
                        <td>
                          {n.created_at
                            ? new Date(n.created_at).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )
                            : ""}
                        </td>
                        <td>
                          <span
                            className={`del-badge ${
                              String(n.status).toLowerCase() === "read"
                                ? "del-badge-success"
                                : "del-badge-warning"
                            }`}
                          >
                            {n.status}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            actions={[
                              {
                                label: "View",
                                icon: "MdVisibility",
                                onClick: () => {
                                  setSelectedNotification(n);
                                  setShowModal(true);
                                },
                                className: "view",
                              },
                              {
                                label:
                                  String(n.status).toLowerCase() === "read"
                                    ? "Mark as Unread"
                                    : "Mark as Read",
                                onClick: () => changeNotificationStatus(n),
                                icon:
                                  String(n.status).toLowerCase() === "read"
                                    ? "MdMarkEmailUnread"
                                    : "MdMarkEmailRead",

                                className: "enable",
                              },
                              {
                                label: "Delete",
                                icon: "MdDelete",
                                onClick: () => deleteNotification(n.id),
                                className: "delete",
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredNotifications.length === 0 && (
                  <div className="del-no-data">No notifications found</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Pagination */}
          <Row>
            <Col xs={12}>
              <Pagination
                totalItems={filteredNotifications.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
        </Container>

        {/* Notification Modal */}
        {showModal && selectedNotification && (
          <div
            className="del-modal-overlay"
            onClick={() => setShowModal(false)}
          >
            <div
              className="del-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="del-modal-header">
                <h3 className="del-modal-title">Notification Details</h3>
                <button
                  className="del-modal-close"
                  onClick={() => setShowModal(false)}
                >
                  <MdClose />
                </button>
              </div>
              <div className="del-modal-body">
                <p>
                  <strong>Title:</strong> {selectedNotification.title}
                </p>
                <p>
                  <strong>Message:</strong> {selectedNotification.message}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {selectedNotification.created_at?.split("T")[0] || ""} at
                  {selectedNotification.created_at
                    ? new Date(
                        selectedNotification.created_at,
                      ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </p>
                <p>
                  <strong>Status:</strong> {selectedNotification.status}
                </p>
              </div>

              <div className="del-modal-footer">
                {/* <button
                  className="custom-btn custom-btn-outline"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </DeliveryPanelLayout>
  );
}

export default DeliveryNotifications;
